// Page Supervision > "Usage des vues" (BI-693) : usage reel des vues iCTI compare au referentiel.
// Donnees : GET /api/usage/views (periode, environnement = hostname + environnement_code, module).
import React, { useEffect, useMemo, useState } from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Modal, Col, Empty, Row, Spin, Table } from "antd";
import { useHistory } from "react-router-dom";

import axios from "../../utils/axios";
import PageEntete, { BarreSlot, Puces, useUrlParams } from "../utils/PageEntete";
import { colonneEtat } from "./UsageBadges";
import UsageFiltres, { construireParams, periodeDefaut } from "./UsageFiltres";
import UsageStats, { HORS, LIB, ORANGE, SEUIL_TENDANCE, Tuile, libelleModule } from "./UsageStats";
import {
  cleEnv,
  comparer,
  formaterDate,
  formaterDureeMs,
  formaterNombre,
  formaterOctets,
  texteSynchro,
  urlReferentiel,
} from "./usageVuesFormat";

const liste = (v) => (Array.isArray(v) ? v : []);
const tri = (cle, rendu) => ({
  sorter: comparer(cle),
  sortDirections: ["descend", "ascend"],
  ...(rendu ? { render: rendu } : {}),
});
const num = (cle, f = formaterNombre) => ({ dataIndex: cle, align: "right", ...tri(cle, f) });

export const colonnesUtilisees = () => [
  { title: "Code", dataIndex: "code", ...tri("code") },
  { title: "Libellé", dataIndex: "label", ...tri("label", (v) => v || "—") },
  colonneEtat((r) => (r.in_repository === false ? "hors" : r.removed ? "supprimee" : "active")),
  { title: "Module", dataIndex: "module_label", ...tri("module_label", (v, r) => libelleModule(r)) },
  { title: LIB.ouvertures, ...num("opens"), defaultSortOrder: "descend" },
  { title: "dont favoris", ...num("favorite_opens") },
  { title: LIB.environnements, ...num("environments") },
  { title: "Durée moy.", ...num("avg_duration_ms", formaterDureeMs) },
  { title: "Durée max", ...num("max_duration_ms", formaterDureeMs) },
  { title: "SQL moy.", ...num("avg_sql_ms", formaterDureeMs) },
  { title: "Volume", ...num("total_bytes", formaterOctets) },
  { title: "Dernière ouverture", dataIndex: "last_opened_at", ...tri("last_opened_at", formaterDate) },
];

const COL_JAMAIS = [
  { title: "Code", dataIndex: "code", ...tri("code") },
  { title: "Libellé", dataIndex: "label", ...tri("label", (v) => v || "—") },
  { title: "Module", dataIndex: "module_label", ...tri("module_label", (v, r) => libelleModule(r)) },
];
const COL_INCONNUES = [
  { title: "Code remonté", dataIndex: "view_code", ...tri("view_code") },
  { title: "Texte", dataIndex: "view_texte", ...tri("view_texte", (v) => v || "—") },
  { title: LIB.ouvertures, ...num("opens"), defaultSortOrder: "descend" },
];

const pagination = { defaultPageSize: 50, showSizeChanger: true, hideOnSinglePage: true };
const vide = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucune donnée sur la période" />;
const somme = (rows) => rows.reduce((s, v) => s + (Number(v && v.opens) || 0), 0);

const REGLES = [
  ["Vue", "identifiée par le nom de son fichier XML (ex. ACTI000127), c'est ce qu'iCTI enregistre."],
  ["Module", "dossier de la vue (modules/<module>/), qu'elle soit dans un menu ou ouverte en cascade ; libellé du module déclaré dans iCTI."],
  ["Nom incohérent", "l'attribut name du XML diffère du nom du fichier (ex. ACTI000127.XML déclare name=\"ACTI000138\"). Information pour corriger iCTI, sans effet sur les comptages."],
  ["Supprimée du dépôt", "la vue n'existe plus dans iCTI ; son historique d'usage reste visible, elle n'apparaît pas dans « Jamais utilisées »."],
  ["Jamais utilisée", "vue du référentiel sans aucune ouverture sur la période et les filtres."],
  ["Inconnue", "vue remontée par un environnement mais absente du référentiel (spécifique client, renommée ou autre version iCTI)."],
  ["Favori", "une ouverture de favori est comptée sur sa vue d'origine, et dans « dont favoris »."],
  ["Environnement", "couple serveur + code d'environnement."],
  ["Vues les plus répandues", "nombre d'environnements qui ont ouvert la vue sur la période, sur le total des environnements actifs."],
  [HORS, "vues remontées dont le module n'est pas déclaré dans le référentiel iCTI ; elles sont regroupées sous un seul groupe, en dernier."],
  ["Référentiel des vues", "liste des vues du dépôt iCTI, consultable dans Configuration › Référentiels › Vues."],
  [LIB.p95, "durée sous laquelle se font 95 % des ouvertures de la vue (p95) ; plus parlant que la moyenne, qui masque les lenteurs ponctuelles."],
  ["Statistiques", `tendances = période choisie vs période précédente de même durée (hausse ou baisse à partir de ±${SEUIL_TENDANCE} %) ; « sans ouverture depuis 30 jours » ignore la période.`],
];

// Meme modele que Configuration : groupes dans l'entete, pastilles des sous-sections dessous.
const GROUPES = [
  { key: "usage", label: "Usage", subs: [{ key: "views", label: "Vues" }] },
  {
    key: "stats",
    label: "Statistiques",
    subs: [
      { key: "dashboard", label: "Tableau de bord" },
      { key: "explore", label: "Exploration" },
    ],
  },
];

// ?tab=usage|stats&sub=views|dashboard|explore ; ancien &layout=explore|dashboard|cockpit|modules|map -> sub.
export function resoudreSelection(params) {
  const group = GROUPES.find((g) => g.key === params.tab) || GROUPES[0];
  let sub = group.subs.find((x) => x.key === params.sub);
  if (!sub && group.key === "stats") sub = group.subs.find((x) => x.key === (params.layout === "explore" ? "explore" : "dashboard"));
  return { group, sub: sub || group.subs[0] };
}

export default function UsageVues() {
  const [showRegles, setShowRegles] = useState(false);
  const [slot, setSlot] = useState(null);
  const [urlParams, majUrl] = useUrlParams();
  const { group, sub } = resoudreSelection(urlParams);
  const vue = group.key;
  const aller = (g, sb) => majUrl({ tab: g, sub: sb, layout: null });
  const [periode, setPeriode] = useState(periodeDefaut);
  const [env, setEnv] = useState(undefined);
  const [module, setModule] = useState(undefined);
  const [etat, setEtat] = useState({ chargement: true, erreur: false, data: null });
  const [envsConnus, setEnvsConnus] = useState([]);
  const [modulesConnus, setModulesConnus] = useState([]);

  const params = construireParams(periode, env, module);
  const cle = params ? JSON.stringify(params) : null;

  // Filtres partages : un seul etat, remonte ici ; la liste des environnements / modules vient de /views.
  useEffect(() => {
    if (!params) return undefined;
    let actif = true;
    setEtat((e) => ({ ...e, chargement: true, erreur: false }));
    axios
      .get("/api/usage/views", { params })
      .then((res) => {
        if (!actif) return;
        const data = (res && res.data) || {};
        setEtat({ chargement: false, erreur: false, data });
        // les listes de filtres ne doivent pas se reduire quand on filtre
        setEnvsConnus((prev) => {
          const m = new Map(prev.map((e) => [e.cle, e]));
          liste(data.environments).forEach((e) => {
            if (!e || !e.hostname) return;
            const c = cleEnv(e.hostname, e.environnement_code);
            m.set(c, { cle: c, label: `${e.hostname} / ${e.environnement_code}` });
          });
          return [...m.values()].sort((a, b) => a.label.localeCompare(b.label));
        });
        // Liste complète du référentiel, déjà triée par libellé par l'API
        setModulesConnus(liste(data.modules).map((m) => ({ code: m.code, label: m.label || m.code })));
      })
      .catch(() => actif && setEtat({ chargement: false, erreur: true, data: null }));
    return () => {
      actif = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle]);

  const filtres = (
    <UsageFiltres
      periode={periode}
      setPeriode={setPeriode}
      env={env}
      setEnv={setEnv}
      module={module}
      setModule={setModule}
      envs={envsConnus}
      modules={modulesConnus}
    />
  );

  return (
    <div className="cti-page">
      <PageEntete
        title="Usage des vues"
        label="Vue"
        aside={
          <Button
            size="small"
            shape="round"
            className="suivi-logs-regles-btn"
            icon={<QuestionCircleOutlined />}
            onClick={() => setShowRegles(true)}
          >
            Règles
          </Button>
        }
        items={GROUPES}
        value={group.key}
        onChange={(key) => aller(key, GROUPES.find((g) => g.key === key).subs[0].key)}
      />
      <Puces label="Section" items={group.subs} value={sub.key} onChange={(key) => aller(group.key, key)} extra={<span className="cti-bar-slot" ref={setSlot} />} />
      <Modal
        title="Règles de lecture"
        open={showRegles}
        onCancel={() => setShowRegles(false)}
        footer={
          <Button type="primary" onClick={() => setShowRegles(false)}>
            Fermer
          </Button>
        }
      >
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {REGLES.map(([t, d]) => (
            <li key={t}>
              <strong>{t}</strong> : {d}
            </li>
          ))}
        </ul>
      </Modal>
      <BarreSlot.Provider value={slot}>
        <div className="cti-panel usage-vues">
          {vue === "stats" ? (
            <UsageStats params={params} filtres={filtres} layout={sub.key} />
          ) : (
            <UsageDetail etat={etat} filtres={filtres} setEnv={setEnv} module={module} />
          )}
        </div>
      </BarreSlot.Provider>
    </div>
  );
}

function UsageDetail({ etat, filtres, setEnv, module }) {
  const history = useHistory();
  const [onglet, setOnglet] = useState("utilisees");

  const d = etat.data || {};
  const vues = liste(d.views);
  const jamais = liste(d.never_used);
  const inconnues = liste(d.unknown);
  const envs = liste(d.environments);
  const depot = d.repository || {};
  // `views` contient deja les vues hors référentiel (in_repository:false) ; `unknown` n'en est qu'un sous-ensemble : ne pas les additionner
  const ouvertures = useMemo(() => somme(vues), [vues]);
  const colEnvs = useMemo(
    () => [
      { title: "Hostname", dataIndex: "hostname", ...tri("hostname") },
      { title: "Environnement", dataIndex: "environnement_code", ...tri("environnement_code") },
      { title: LIB.ouvertures, ...num("opens"), defaultSortOrder: "descend" },
      { title: "Vues utilisées", ...num("views_used") },
      { title: "Dernière ouverture", dataIndex: "last_opened_at", ...tri("last_opened_at", formaterDate) },
    ],
    []
  );

  const tableau = (cols, rows, cle, extra) => (
    <Table
      size="small"
      rowKey={cle}
      columns={cols}
      dataSource={rows}
      pagination={pagination}
      locale={{ emptyText: vide }}
      {...extra}
    />
  );

  const items = [
    { key: "utilisees", label: `Vues utilisées (${vues.length})`, children: tableau(colonnesUtilisees(), vues, "code") },
    { key: "jamais", label: `${LIB.jamais} (${jamais.length})`, children: tableau(COL_JAMAIS, jamais, "code") },
    { key: "inconnues", label: `Inconnues (${inconnues.length})`, children: tableau(COL_INCONNUES, inconnues, "view_code") },
    {
      key: "envs",
      label: `Par environnement (${envs.length})`,
      children: tableau(colEnvs, envs, (r) => cleEnv(r.hostname, r.environnement_code), {
        onRow: (r) => ({
          onClick: () => setEnv(cleEnv(r.hostname, r.environnement_code)),
          style: { cursor: "pointer" },
        }),
      }),
    },
  ];

  return (
    <div>
      {etat.data && depot.synced_at && (
        <div data-testid="stat-synchro" style={{ color: "#6b7785", fontSize: 12, marginBottom: 12 }}>
          {texteSynchro(depot)}
        </div>
      )}
      {etat.data && (
        <Row gutter={[12, 12]} className="usage-synthese" style={{ marginBottom: 12 }}>
          {[
            { id: "stat-referentiel", titre: "Vues du référentiel", valeur: formaterNombre(depot.views), lien: true },
            { id: "stat-utilisees", titre: "Vues utilisées", valeur: formaterNombre(vues.filter((v) => v.in_repository).length), sous: `sur ${formaterNombre(depot.views)} vues`, onglet: "utilisees" },
            { id: "stat-jamais", titre: LIB.jamais, valeur: formaterNombre(jamais.length), alerte: jamais.length > 0, onglet: "jamais" },
            { id: "stat-inconnues", titre: "Inconnues", valeur: formaterNombre(inconnues.length), alerte: inconnues.length > 0, onglet: "inconnues" },
            { id: "stat-envs", titre: LIB.environnements, valeur: formaterNombre(envs.length), onglet: "envs" },
            { id: "stat-ouvertures", titre: LIB.ouvertures, valeur: formaterNombre(ouvertures) },
          ].map(({ alerte, onglet: cible, lien, ...t }) => (
            <Col key={t.id} xs={12} sm={8} md={4}>
              <Tuile
                {...t}
                couleurValeur={alerte ? ORANGE : undefined}
                lien={lien}
                onClick={cible ? () => setOnglet(cible) : lien ? () => history && history.push(urlReferentiel(module)) : undefined}
                actif={!!cible && cible === onglet}
              />
            </Col>
          ))}
        </Row>
      )}
      {filtres}
      {etat.erreur && (
        <Alert type="error" showIcon message="Impossible de charger l'usage des vues." style={{ marginBottom: 16 }} />
      )}
      {etat.chargement && !etat.data ? (
        <Spin tip="Chargement…" />
      ) : etat.data ? (
        <Spin spinning={etat.chargement}>
          {!depot.synced_at && (
            <Alert
              type="warning"
              showIcon
              message="Le référentiel des vues n'a jamais été synchronisé."
              style={{ marginBottom: 16 }}
            />
          )}
          {/* Les tuiles cliquables tiennent lieu d'onglets */}
          {items.find((i) => i.key === onglet).children}
        </Spin>
      ) : null}
    </div>
  );
}
